import { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
import ActiveProjectsService from "@/services/activeProjectsService";
import mapPackagingFormToApi from "../utils/mapPackagingFormToApi";
import { toIntegerPercentApiValue, toGramWeightFormValue } from "../utils/fieldInputUtils";
import type { IPackagingData, IPrinterDielineDetailsData, IPackagingDocument, ICountryOfOriginStatementsData, IKosherSymbolData, IRegionSpecificIconsData, IClaimsDisclaimerStatementsData, ISafetyProcessingEnvironmentalData, IRecyclingIconsData, IStorageHandlingDistributionData, IProductLabellingDetailsData, IBottleRedemptionInformationData, INonCopyInformationData, IPackagingComponent, IIconsAndClaimsData } from "../types/OtherLabeling.types";
import { parseSingleInputSelection } from "@/utils/helper";
import {
  parseActiveProjectMultiSelectIds,
  formatActiveProjectMultiSelectIdsForSave,
} from "@/components/ActiveProjects/utils/helper";

type UsePackagingOnloadReturn = {
  data: Partial<IPackagingData> | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  save: (formData: IPackagingData) => Promise<any>;
};

function mapDocumentsToPackagingDocuments(docs?: Array<{ documentTitle?: string; document?: { id?: string } }>): IPackagingDocument[] {
  if (!Array.isArray(docs)) return [];
  return docs.map((d, idx) => ({
    id: String(d?.document?.id ?? `doc-${idx}`),
    fileName: String(d?.documentTitle ?? `Document ${idx + 1}`),
    type: "",
    uploadDate: new Date(),
  }));
}

function safeString(v: any, fallback: string = ""): string { return (v ?? fallback) as string; }

function getIconsAndClaimsFromApi(api: any): unknown[] {
  const source = api?.iconsAndClaims ?? api?.iconclaims;
  return Array.isArray(source) ? source : [];
}

/** Normalizes GET icons/claims (API may use `iconclaims` + `cautionStatement`). */
function mapIconsAndClaimsFromApi(api: any): IIconsAndClaimsData[] {
  return getIconsAndClaimsFromApi(api).map((i: any) => ({
    id: i?.id,
    productClaims: safeString(i?.productClaims),
    cautionStatements: safeString(i?.cautionStatements ?? i?.cautionStatement),
    directionsForUse: safeString(i?.directionsForUse),
  }));
}


export function mapOnloadApiToPackagingData(api: any): Partial<IPackagingData> {
  if (!api || typeof api !== "object") return {};

  const recyclingIconsData: any[] = Array.isArray(api?.recyclingIcons) ? api.recyclingIcons.map((r: any) => ({
    id: r?.id,
    text: safeString(r?.text),
    supportingText: safeString(r?.supportingText),
    useOnPack: safeString(r?.useOnPack || ''),
  })) : [];

  const pldSrc: any = Array.isArray(api?.productLabelingDetails)
    ? api?.productLabelingDetails?.[api.productLabelingDetails.length - 1]
    : (api?.productLabelingDetails || {});
  const productLabellingDetailsData: IProductLabellingDetailsData = {
    id: pldSrc?.id,
    statementOfIdentity: safeString(pldSrc?.productDescriptor),
    exactNameOnPackaging: "",
    productStory: safeString(pldSrc?.productStory),
    notesToDesign: safeString(pldSrc?.notesToDesign),
  } as any;

  const wusSrc: any = api?.weightsUnitsSpecification || api?.weightUnitsSpecification || {};
  const weightUnitsSpecificationData = {
    quantities: [safeString(wusSrc?.weightVolumePackQty || api?.weightVolumePackQty), safeString(wusSrc?.weightVolumePackQtyUom || api?.weightVolumePackQtyUom)].filter(Boolean).join(" ").trim(),
    declaredQuantityType: safeString(wusSrc?.declaredQuantityWeightVolumeType || api?.declaredQuantityWeightVolumeType) as any,
    declaredQuantityPrintingBy: safeString(wusSrc?.declaredQuantityPrintingBy || api?.declaredQuantityPrintingBy) as any,
    netDeclaredWeightVolume: safeString(wusSrc?.onPackDeclaredNetQuantityWeightVolume || api?.onPackDeclaredNetQuantityWeightVolume),
    locationOnPack: safeString(wusSrc?.locationOnPack || api?.locationOnPack),
    drainedWeight: safeString(wusSrc?.drainedWeight || api?.drainedWeight),
  } as any;

  const safetyProcessingEnvStatements: any[] = Array.isArray(api?.safetyProcessingEnvStatements) ? api.safetyProcessingEnvStatements.map((s: any) => {
    const parseStatements = (raw: any): any[] => {
      if (!raw) return [];
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : [raw]);
        if (Array.isArray(parsed)) {
          return parsed.map((sel: any) => {
            if (!sel) return sel;
            const optionSrc: any = sel.option || {};
            const label = typeof optionSrc?.label === 'string' ? optionSrc.label.replace(/\(1\)/g, '{}') : optionSrc?.label;
            return {
              option: { ...optionSrc, label },
              inputs: Array.isArray(sel.inputs) ? sel.inputs : [],
            };
          });
        }
        return [];
      } catch { return []; }
    };
    return {
      id: s?.id,
      format: safeString(s?.format),
      text: safeString(s?.text),
      useOnPack: safeString(s?.useOnPack || ''),
      statements: parseStatements(s?.text),
    };
  }) : [];

  const storageHandlingDistributionData: any[] = Array.isArray(api?.storageHandlingDistributionStatements) ? api.storageHandlingDistributionStatements.map((s: any) => {
    // Parse MultiInputDropdown selections from "text" like we do for claims/disclaimers
    const parseSelections = (raw: any): any[] => {
      if (!raw) return [];
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : [raw]);
        if (Array.isArray(parsed)) {
          return parsed.map((sel: any) => {
            if (!sel) return sel;
            const optionSrc: any = sel.option || {};
            const label = typeof optionSrc?.label === 'string' ? optionSrc.label.replace(/\(1\)/g, '{}').replace(/\{s\}/g, '{}').replace(/\{m\}/g, '{}').replace(/\{l\}/g, '{}') : optionSrc?.label;
            return {
              option: { ...optionSrc, label },
              inputs: Array.isArray(sel.inputs) ? sel.inputs : [],
            };
          });
        }
        return [];
      } catch { return []; }
    };
    const keepFrozenRaw = s?.keepFrozenStatement;
    const isKeepFrozenRequired = String(keepFrozenRaw).toLowerCase() === 'true';
    return {
      id: s?.id,
      format: safeString(s?.format),
      text: safeString(s?.text),
      isKeepFrozenRequired,
      formats: parseSelections(s?.text),
      reasonCooLabeling: safeString(s?.reasonCooLabeling)
    };
  }) : [];

  const printerEndpointArray: any[] | undefined = Array.isArray(api?.printerDetails)
    ? api.printerDetails
    : (Array.isArray((api as any)?.printerDielineDetails) ? (api as any).printerDielineDetails : undefined);

  const printerDielineDetailsData: any[] = Array.isArray(printerEndpointArray) ? printerEndpointArray.map((p: any) => ({
    id: p?.id,
    printerCompanyName: safeString(p?.companyName ?? p?.printerCompanyName),
    printerContactName: safeString(p?.printerContactName),
    printerEmailAddress: safeString(p?.emailAddress ?? p?.printerEmailAddress),
    printerPhone: safeString(p?.phone ?? p?.printerPhone),
    printerCountry: safeString(p?.country ?? p?.printerCountry),
    printerAddressLine1: safeString(p?.addressLineOne ?? p?.printerAddressLine1),
    printerAddressLine2: safeString(p?.addressLineTwo ?? p?.printerAddressLine2),
    printerCity: safeString(p?.city ?? p?.printerCity),
    printerStateRegionProvince: safeString(p?.state ?? p?.printerStateRegionProvince),
    printerZipPostalCode: safeString(p?.zip ?? p?.printerZipPostalCode),
    packagingComponent: safeString(p?.packagingComponent),
    printSubstrate: safeString(p?.printSubstrate),
    printProcess: safeString(p?.printProcess),
    maxNumberOfColors: safeString(p?.color ?? p?.colors ?? p?.maxNumberOfColors),
    colors: safeString(p?.color ?? p?.colors ?? p?.maxNumberOfColors), // Keep for backward compatibility
  })) : [];

  // Artwork Approver Details: single entry section; load from top-level API fields
  const artworkApproverDetailsData = {
    artworkApproverName: safeString((api as any)?.artworkApproverName),
    artworkApproverEmail: safeString((api as any)?.artworkApproverEmail),
    artworkApproverPhone: safeString((api as any)?.artworkApproverPhoneNumber),
  };

  const organicClaim: any[] = Array.isArray(api?.organicClaim) ? api.organicClaim.map((o: any) => ({
    id: o?.id,
    icon: safeString(o?.icon),
    useOnPack: safeString(o?.useOnPack || ''),
    supportingTextForDeclaration: safeString(o?.supportingTextForDeclaration),
  })) : [];

  const organicCertifier: any[] = Array.isArray(api?.organicCertifier) ? api.organicCertifier.map((o: any) => ({
    id: o?.id,
    icon: safeString(o?.icon),
    useOnPack: safeString(o?.useOnPack || ''),
    supportingTextForDeclaration: safeString(o?.supportingTextForDeclaration),
  })) : [];

  const bottleRedemptionInformation: any[] = Array.isArray(api?.bottleRedemptionInformation) ? api.bottleRedemptionInformation.map((b: any) => ({
    id: b?.id,
    bottleRedemptionStates: parseActiveProjectMultiSelectIds(b?.bottleRedemptionStates),
    useOnPack: safeString(b?.useOnPack || ''),
    supportingTextForDeclaration: safeString(b?.supportingTextForDeclaration),
  })) : [];

  const nciSrc: any = (api as any)?.nonCopyInformation || {};
  const nonCopyInformationData: INonCopyInformationData = {
    designComments: safeString(nciSrc?.designComments ?? api?.designComments),
    reasonForIssue: safeString(nciSrc?.reasonForIssue ?? ((api as any)?.reasonForIssue ?? (api as any)?.resaonForIssue)),
    designTeam: safeString(nciSrc?.designTeam ?? api?.designTeam),
    anyOtherInfoBackOfPack: safeString(nciSrc?.anyOtherInfoBackOfPack ?? (api as any)?.anyOtherInfoBackOfPack),
    photographyDateProductsReadyToShoot: safeString(nciSrc?.photography ?? (api as any)?.photography),
  };

  // Cooking instructions mapping (GET)
  const cookingInstructionsData = {
    comments: safeString((api as any)?.cookingInstructions?.comments || (api as any)?.comments)
  } as any;
  
  const countryOfOriginStatementsData: any[] = Array.isArray(api?.countryOfOriginStatements) ? api.countryOfOriginStatements.map((c: any) => {
    const stmt1 = parseSingleInputSelection(c?.countryOfOriginStatement1);
    const stmt2 = parseSingleInputSelection(c?.countryOfOriginStatement2);
    return {
      id: c?.id,
      countryOfOriginStatement1: stmt1,
      countryOfOriginStatement2: stmt2,
      countryOfOriginCountry1: parseActiveProjectMultiSelectIds(c?.countryOfOriginCountry1),
      countryOfOriginCountry2: parseActiveProjectMultiSelectIds(c?.countryOfOriginCountry2),
      reasonForCountryOfOrigin: safeString(c?.reasonforCountryofOriginLabeling),
      cooRequiredOnLabel: safeString(c?.cooRequiredOnLabel),
      supportingText: safeString(c?.supportingText),
    };
  }) : [];

  const kosherSymbolData: any[] = Array.isArray(api?.kosherSymbol) ? api.kosherSymbol.map((k: any) => ({
    id: k?.id,
    onPackIcon: safeString(k?.onPackIcon ?? k?.icon ?? ''),
    useOnPack: safeString(k?.useOnPack || ''),
    supportingTextForDeclaration: safeString(k?.supportingTextDeclaration),
  })) : [];
  
  const kosherDesignation: any[] = Array.isArray(api?.kosherDesignation) ? api.kosherDesignation.map((k: any) => ({
    id: k?.id,
    onPackIcon: safeString(k?.onPackIcon ?? k?.icon ?? ''),
    useOnPack: safeString(k?.useOnPack || ''),
    supportingTextForDeclaration: safeString(
      k?.supportingTextDeclaration ?? k?.supportingTextForDeclaration
    ),
  })) : [];

  const productSpecificSrc: any[] | undefined = Array.isArray((api as any)?.productSpecificIconsLegends)
    ? (api as any).productSpecificIconsLegends
    : (Array.isArray((api as any)?.productSpecificIconsStamps) ? (api as any).productSpecificIconsStamps : undefined);
  const regionSpecificIconsData: any[] = Array.isArray(productSpecificSrc) ? productSpecificSrc.map((r: any) => ({
    id: r?.id,
    site: safeString(r?.site?.id ?? r?.site ?? ''),
    onPackIcons: safeString(r?.onPackIcons),
    useOnPack: safeString(r?.useOnPack || ''),
    supportingTextForDeclaration: safeString(r?.supportingTextForDeclaration),
  })) : [];

  const claimsDisclaimerStatementsData: any[] = Array.isArray(api?.claimsDisclaimerStatements) ? api.claimsDisclaimerStatements.map((c: any) => {
    const parseStatements = (raw: any): any[] => {
      if (!raw) return [];
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : [raw]);
        if (Array.isArray(parsed)) {
          return parsed.map((sel: any) => {
            if (!sel) return sel;
            const optionSrc: any = sel.option || {};
            const label = typeof optionSrc?.label === 'string' ? optionSrc.label.replace(/\(1\)/g, '{}') : optionSrc?.label;
            return {
              option: { ...optionSrc, label },
              inputs: Array.isArray(sel.inputs) ? sel.inputs : [],
            };
          });
        }
        return [];
      } catch { return []; }
    };
    return {
      id: c?.id,
      useOnPack: safeString(c?.useOnPack || ''),
      format: safeString(c?.format),
      statements: parseStatements(c?.text),
      // Include HABA-specific fields if they exist in the API response
      cosmeticWarningPhrase: safeString(c?.cosmeticWarningPhrase || ''),
      candleWarningPhrase: safeString(c?.candleWarningPhrase || ''),
      suitableForVegetarians: safeString(c?.suitableForVegetarians || ''),
      suitableForVegans: safeString(c?.suitableForVegans || ''),
    };
  }) : [];

  const codingStatementsData: any[] = (() => {
    const raw = api?.codingStatement;
    if (Array.isArray(raw)) {
      return raw.map((entry: any) => ({
        id: entry?.id,
        statement: safeString(entry?.statement),
        useOnPack: safeString(entry?.useOnPack || ''),
      }));
    }
    if (raw && typeof raw === 'object') {
      return [{
        id: raw?.id,
        statement: safeString(raw?.statement),
        useOnPack: safeString(raw?.useOnPack || ''),
      }];
    }
    return [];
  })();

  const iconsAndClaimsData = mapIconsAndClaimsFromApi(api);

  const documentsData = {
    packagingDocuments: mapDocumentsToPackagingDocuments(api?.attachments?.length ? api.attachments : api?.documents),
    dielineDocuments: mapDocumentsToPackagingDocuments(api?.dielineDocuments),
    traceabilityLabelDocuments: mapDocumentsToPackagingDocuments(api?.traceabilityLabel),
    attachmentsSupportingText: "",
    dielineDocumentsSupportingText: "",
  };

  const data: any = {
    recyclingIconsData,
    productLabellingDetailsData,
    weightUnitsSpecificationData,
    safetyProcessingEnvStatements,
    storageHandlingDistributionData,
    printerDielineDetailsData,
    artworkApproverDetailsData,
    organicClaim,
    organicCertifier,
    bottleRedemptionInformation,
    nonCopyInformationData,
    cookingInstructionsData,
    countryOfOriginStatementsData,
    kosherSymbolData,
    kosherDesignation,
    regionSpecificIconsData,
    claimsDisclaimerStatementsData,
    codingStatementsData,
    iconsAndClaimsData,
    documentsData,
  } as Partial<IPackagingData>;

  // Also inject endpoint-aligned top-level fields so saves can pass-through
  // Weight and declared quantity fields
  data.weightVolumePackQty = safeString(api?.weightVolumePackQty);
  data.weightVolumePackQtyUom = safeString(api?.weightVolumePackQtyUom);
  data.declaredQuantityWeightVolumeType = safeString(api?.declaredQuantityWeightVolumeType);
  data.declaredQuantityPrintingBy = safeString(api?.declaredQuantityPrintingBy);
  data.onPackDeclaredNetQuantityWeightVolume = safeString(api?.onPackDeclaredNetQuantityWeightVolume);
  data.locationOnPack = safeString(api?.locationOnPack);
  data.drainedWeight = safeString(api?.drainedWeight);

  // Arrays with endpoint keys
  data.recyclingIcons = recyclingIconsData?.map((r) => ({ text: r.text, supportingText: r.supportingText, useOnPack: r.useOnPack }));
  data.productLabelingDetails = Array.isArray(api?.productLabelingDetails) && api.productLabelingDetails.length
    ? api.productLabelingDetails
    : [
        {
          id: data?.productLabellingDetailsData?.id,
          productDescriptor: data?.productLabellingDetailsData?.statementOfIdentity || '',
          productStory: data?.productLabellingDetailsData?.productStory || '',
          notesToDesign: data?.productLabellingDetailsData?.notesToDesign || '',
        }
      ];
  data.safetyProcessingEnvStatements = safetyProcessingEnvStatements;
  data.storageHandlingDistributionStatements = Array.isArray(api?.storageHandlingDistributionStatements)
    ? api.storageHandlingDistributionStatements
    : storageHandlingDistributionData?.map((s) => ({
        id: (s as any)?.id,
        format: s.format || '',
        text: s.text || '',
      }));

  data.codingStatements = Array.isArray(api?.codingStatement)
    ? api.codingStatement
    : codingStatementsData?.map((s) => ({ id: (s as any)?.id, statement: s.statement || '', useOnPack: s.useOnPack || '' }));
  data.printerDetails = Array.isArray(printerEndpointArray) && printerEndpointArray.length ? printerEndpointArray : printerDielineDetailsData?.map((p: any) => ({
    companyName: p.printerCompanyName,
    printerContactName: p.printerContactName,
    emailAddress: p.printerEmailAddress,
    phone: p.printerPhone,
    country: p.printerCountry,
    addressLineOne: p.printerAddressLine1,
    addressLineTwo: p.printerAddressLine2,
    city: p.printerCity,
    state: p.printerStateRegionProvince,
    zip: p.printerZipPostalCode,
    packagingComponent: p.packagingComponent,
    printSubstrate: p.printSubstrate,
    printProcess: p.printProcess,
    color: p.colors,
  }));
  data.organicClaim = Array.isArray(api?.organicClaim) && api.organicClaim.length ? api.organicClaim : organicClaim?.map((o) => ({
    supportingTextForDeclaration: o.supportingTextForDeclaration,
    icon: o.icon,
    useOnPack: o.useOnPack,
  }));
  data.organicCertifier = Array.isArray(api?.organicCertifier) && api.organicCertifier.length ? api.organicCertifier : organicCertifier?.map((o) => ({
    id: o.id,
    supportingTextForDeclaration: o.supportingTextForDeclaration,
    icon: o.icon,
    useOnPack: o.useOnPack,
  }));
  data.bottleRedemptionInformation = bottleRedemptionInformation;
  data.countryOfOriginStatements = Array.isArray(api?.countryOfOriginStatements) ? api.countryOfOriginStatements : countryOfOriginStatementsData?.map((c) => ({
    id: c.id,
    countryOfOriginStatement1: (c.countryOfOriginStatement1 as any)?.label || (c.countryOfOriginStatement1 as any)?.value || '',
    countryOfOriginCountry1: formatActiveProjectMultiSelectIdsForSave(c.countryOfOriginCountry1),
    countryOfOriginStatement2: (c.countryOfOriginStatement2 as any)?.label || (c.countryOfOriginStatement2 as any)?.value || '',
    countryOfOriginCountry2: formatActiveProjectMultiSelectIdsForSave(c.countryOfOriginCountry2),
    reasonforCountryofOriginLabeling: c.reasonForCountryOfOrigin,
    supportingText: c.supportingText,
  }));
  data.kosherSymbol = Array.isArray(api?.kosherSymbol) ? api.kosherSymbol : kosherSymbolData?.map((k) => ({
    id: k.id,
    onPackIcon: k.onPackIcon,
    useOnPack: k.useOnPack,
    supportingTextDeclaration: k.supportingTextForDeclaration,
  }));
  // Form `kosherDesignation` is already set on `data` from the `kosherDesignation` const (~252, ~341). No extra branch here (unlike `data.kosherSymbol` pass-through).
  data.productSpecificIconsStamps = Array.isArray((api as any)?.productSpecificIconsStamps)
    ? (api as any).productSpecificIconsStamps
    : Array.isArray((api as any)?.productSpecificIconsLegends)
      ? (api as any).productSpecificIconsLegends
      : regionSpecificIconsData?.map((r) => ({
          id: r.id,
          site: r.site,
          onPackIcons: r.onPackIcons,
          useOnPack: r.useOnPack,
          supportingTextForDeclaration: r.supportingTextForDeclaration,
        }));
  data.claimsDisclaimerStatements = Array.isArray(api?.claimsDisclaimerStatements) ? api.claimsDisclaimerStatements : claimsDisclaimerStatementsData?.map((c) => ({
    id: c.id,
    format: '',
    text: '',
    useOnPack: c.useOnPack,
  }));
  const iconClaimsFromApi = getIconsAndClaimsFromApi(api);
  data.iconsAndClaims =
    iconClaimsFromApi.length > 0
      ? iconClaimsFromApi
      : iconsAndClaimsData?.map((i) => ({
          id: i.id,
          productClaims: i.productClaims,
          cautionStatements: i.cautionStatements,
          directionsForUse: i.directionsForUse,
        }));

  // Documents arrays
  const docsFromApi = mapDocumentsToPackagingDocuments(api?.documents);
  const attachmentsFromApi = mapDocumentsToPackagingDocuments(api?.attachments);
  const dielineFromApi = mapDocumentsToPackagingDocuments(api?.dielineDocuments);
  const traceabilityFromApi = mapDocumentsToPackagingDocuments(api?.traceabilityLabel);
  data.id = api?.id ?? '';
  data.documents = docsFromApi.map(d => ({ documentTitle: d.fileName, document: { id: d.id } }));
  data.attachments = attachmentsFromApi.map(d => ({ documentTitle: d.fileName, document: { id: d.id } }));
  data.dielineDocuments = dielineFromApi.map(d => ({ documentTitle: d.fileName, document: { id: d.id } }));
  data.traceabilityLabel = traceabilityFromApi.map(d => ({
    documentTitle: d.fileName,
    document: { id: d.id },
  }));

  return data;
}

export function usePackagingOnload(productId?: string | number, variantId?: string | number): UsePackagingOnloadReturn {
  const [data, setData] = useState<Partial<IPackagingData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { startLoading, stopLoading } = useGlobalStateContext();

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    startLoading();
    setError(null);
    try {
      const onloadResp = await ActiveProjectsService.getPackagingDetails(String(productId));
      const mapped = mapOnloadApiToPackagingData(onloadResp);
      setData(mapped);
    } catch (e: any) {
      setError(e?.message || 'Failed to load packaging onload data');
      setData(null);
    } finally {
      setLoading(false);
      stopLoading();
    }
  }, [productId, variantId]);

  useEffect(() => { load(); }, [load]);
  const save = useCallback(async (formData: IPackagingData) => {
    if (!productId) throw new Error('Missing productId');
    const payload = mapPackagingFormToApi(formData);
    return ActiveProjectsService.savePackagingData(String(productId), payload);
  }, [productId]);

  const api: UsePackagingOnloadReturn = useMemo(() => ({ data, loading, error, reload: load, save }), [data, loading, error, load, save]);
  return api;
}

export default usePackagingOnload;


